/** The category's "Activities in this category" panel (plan §4): the panel under the
 *  category's one card, full width, read-only, an outlined card — the activity record's
 *  "Where it's used" panel for a record whose only relation is its activities.
 *
 *  - Header: "Activities in this category" · how many (a muted tabular caption) · Open in
 *    Activities › (the activities grid filtered by this category).
 *  - Its first ten activities, by name: each a link to the activity, with its type's one
 *    word and its country, then "N more in Activities ›".
 *  - Empty: "No activities in this category — safe to delete" (a delete only ever
 *    uncategorises them, plan §4). Failed: what happened, and Retry.
 *  - Loading: the rows the loaded panel will show, from the detail's `activity_count` —
 *    min(count, 10) rows on the loaded rows' own boxes, the "more" line past ten, the empty
 *    line's place for none — so the rows land where the skeleton stood.
 *
 *  The count shows from the detail payload while the rows load, so the header keeps one
 *  height and one meaning in every state. `ActivitiesInCategoryPanelSkeleton` is the whole
 *  panel while the page itself loads (no detail yet). */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Card, CardContent, Link, Skeleton, Typography } from '@mui/material';
import { useId } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { ActivityRow } from '@/api/activities';
import type { ActivityCategoryRow } from '@/api/activityCategories';

import {
  ACTIVITIES_EMPTY,
  ACTIVITIES_FIRST,
  activitiesSkeleton,
  activityCountLabel,
  activityRowFacts,
  activityRowHref,
  activityRowName,
  categoryActivitiesHref,
  categoryActivityCount,
  type CategoryActivities,
} from './activitiesInCategoryModel';

export const CATEGORY_ACTIVITIES_ANCHOR_ID = 'category-activities';

/** The header row's height in every state, so loading, empty and full panels share it. */
const HEADER_MIN_PX = 30;

const CONTENT_SX = { display: 'grid', gap: 1.5 } as const;
const HEADER_SX = {
  display: 'flex',
  alignItems: 'center',
  columnGap: 1.5,
  rowGap: 0.5,
  flexWrap: 'wrap',
  minHeight: HEADER_MIN_PX,
} as const;
/** The rows' block, the list and a row: one `body2` line each (the skeleton's rows too). */
const BODY_SX = { px: 1, minWidth: 0, fontVariantNumeric: 'tabular-nums' } as const;
const ROWS_SX = { listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25 } as const;
const ROW_SX = { minWidth: 0, typography: 'body2', overflowWrap: 'anywhere' } as const;
const EMPTY_SX = { px: 1 } as const;
/** The skeleton rows' widths, so they read as names of different lengths. */
const SKELETON_ROW_WIDTHS = ['62%', '48%', '70%', '54%', '66%'];
/** The rows a page skeleton shows when no cached grid row says how many the category holds. */
const UNKNOWN_COUNT_ROWS = 3;

export interface ActivitiesInCategoryPanelProps {
  detail: Pick<ActivityCategoryRow, 'id' | 'tenant' | 'activity_count'>;
  /** The category's first activities (`GET /activities/?category=<id>`, by name);
   *  undefined while they load. */
  activities: CategoryActivities | undefined;
  /** The rows could not be loaded. */
  failed?: boolean;
  onRetry?: () => void;
}

export function ActivitiesInCategoryPanel({
  detail,
  activities,
  failed = false,
  onRetry,
}: ActivitiesInCategoryPanelProps) {
  const headingId = useId();
  const href = categoryActivitiesHref(detail);
  const count = categoryActivityCount(activities, detail);

  let body;
  if (failed && !activities) {
    body = (
      <Box
        role="alert"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, color: 'error.main' }}
        data-category-activities-error
      >
        <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
        <Typography variant="body2" component="span" color="text.primary">
          Couldn’t load the activities in this category.
        </Typography>
        {onRetry && (
          <Button size="small" onClick={onRetry} sx={{ minWidth: 0, py: 0 }}>
            Retry
          </Button>
        )}
      </Box>
    );
  } else if (!activities) {
    body = (
      <Box role="status" aria-label="Loading the activities in this category">
        <RowsSkeleton count={count} />
      </Box>
    );
  } else if (activities.results.length === 0) {
    body = (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={EMPTY_SX}
        data-category-activities-empty
      >
        {ACTIVITIES_EMPTY}
      </Typography>
    );
  } else {
    const shown = activities.results.slice(0, ACTIVITIES_FIRST);
    const more = Math.max(count, activities.results.length) - shown.length;
    body = (
      <Box sx={BODY_SX}>
        <Box component="ul" sx={ROWS_SX}>
          {shown.map((row) => (
            <Row key={row.id} row={row} />
          ))}
        </Box>
        {more > 0 && (
          <Link
            component={RouterLink}
            to={href}
            underline="hover"
            variant="caption"
            sx={{ fontWeight: 600 }}
            data-category-activities-more
          >
            {`${more.toLocaleString('en-US')} more in Activities ›`}
          </Link>
        )}
      </Box>
    );
  }

  return (
    <Box
      component="section"
      id={CATEGORY_ACTIVITIES_ANCHOR_ID}
      tabIndex={-1}
      aria-labelledby={headingId}
      data-category-activities
      sx={{
        outline: 'none',
        borderRadius: 1,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
      }}
    >
      <Card variant="outlined">
        <CardContent sx={CONTENT_SX}>
          <Box sx={HEADER_SX}>
            <Typography
              id={headingId}
              variant="subtitle2"
              component="h2"
              sx={{ color: 'text.secondary', m: 0 }}
            >
              Activities in this category
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 1, fontVariantNumeric: 'tabular-nums' }}
              data-category-activities-count
            >
              {count > 0 ? activityCountLabel(count) : ''}
            </Typography>
            <Link
              component={RouterLink}
              to={href}
              underline="hover"
              sx={{ typography: 'body2', fontWeight: 600 }}
            >
              Open in Activities ›
            </Link>
          </Box>
          {body}
        </CardContent>
      </Card>
    </Box>
  );
}

function Row({ row }: { row: ActivityRow }) {
  const href = activityRowHref(row);
  const name = activityRowName(row);
  const facts = activityRowFacts(row);
  return (
    <Box component="li" sx={ROW_SX} data-category-activity-row={row.id}>
      {href ? (
        <Link
          component={RouterLink}
          to={href}
          underline="hover"
          color="text.primary"
          sx={{ fontWeight: 600 }}
        >
          {name}
        </Link>
      ) : (
        name
      )}
      {facts.length > 0 && (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {` · ${facts.join(' · ')}`}
        </Box>
      )}
    </Box>
  );
}

/** The rows while they load, for a category holding `count` activities: the rows the loaded
 *  panel shows on their own boxes, the "more" line past ten, or the empty line's place. Not a
 *  list: the status around it says what loads. */
function RowsSkeleton({ count }: { count: number }) {
  const { rows, more } = activitiesSkeleton(count);
  if (rows === 0) {
    return (
      <Typography variant="body2" sx={EMPTY_SX} data-category-activities-skeleton-empty>
        <Skeleton width="45%" />
      </Typography>
    );
  }
  return (
    <Box sx={BODY_SX}>
      <Box sx={ROWS_SX}>
        {Array.from({ length: rows }, (_, i) => (
          <Box key={i} sx={ROW_SX} data-category-activities-skeleton-row>
            <Skeleton width={SKELETON_ROW_WIDTHS[i % SKELETON_ROW_WIDTHS.length]} />
          </Box>
        ))}
      </Box>
      {more && (
        <Typography variant="caption" component="span" data-category-activities-skeleton-more>
          <Skeleton width={150} sx={{ display: 'inline-block' }} />
        </Typography>
      )}
    </Box>
  );
}

/** The whole panel while the record page loads (no detail yet): its card and header row, and
 *  its rows for `count` activities — the categories grid's cached row says how many; unknown,
 *  three. */
export function ActivitiesInCategoryPanelSkeleton({ count }: { count: number | null }) {
  return (
    <Card variant="outlined" data-category-activities-skeleton>
      <CardContent sx={CONTENT_SX}>
        <Box sx={HEADER_SX}>
          <Typography variant="subtitle2" component="div" sx={{ m: 0, flex: 1 }}>
            <Skeleton width={180} />
          </Typography>
          <Typography variant="body2" component="div">
            <Skeleton width={130} />
          </Typography>
        </Box>
        <RowsSkeleton count={count ?? UNKNOWN_COUNT_ROWS} />
      </CardContent>
    </Card>
  );
}
